import {
  ChangeEvent,
  ClipboardEvent,
  DragEvent,
  KeyboardEvent,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Menu as HeadlessMenu } from '@headlessui/react';
import styled from 'styled-components';

import { AudioRecorderButton } from '@components/audio-recorder-button';
import { FileIconButton } from '@components/file-icon-button';
import { IconButton } from '@components/icon-button';
import { IconAiAgent } from '@components/icons/ai-agent';
import { IconAiChatMode } from '@components/icons/ai-chat-mode';
import { IconArrowUp } from '@components/icons/arrow-up';
import { IconCheckboxChecked } from '@components/icons/checkbox-checked';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconDropdown } from '@components/icons/dropdown';
import { Menu } from '@components/menu';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { getImageExtensionFromMimeType } from '@utils/assets';
import { pxToRem } from '@utils/converters';

import { useTranscribeAudio } from 'api/ai-builder/hooks';
import { AIBuilderMode } from 'api/ai-builder/requests';
import { getActionLabel } from 'ui/actions/action-config';

import { FileTag } from './file-tag';

import type { JourneyNode } from '../../node-types/types';
import { type PendingFile } from '../hooks';

type InputBoxProps = {
  value: string;
  isDisabled?: boolean;
  isGenerating?: boolean;
  placeholder?: string;
  textAreaRef: RefObject<HTMLTextAreaElement>;
  inputRowRef: RefObject<HTMLDivElement>;
  buttonsRowRef: RefObject<HTMLDivElement>;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  onInterrupt?: () => void;
  onResize: () => void;
  files: PendingFile[];
  onFileSelected: (file: File, displayName?: string) => void;
  onFileRemove: (fileId: string) => void;
  errorText?: string;
  onClearError?: () => void;
  selectedNode?: JourneyNode;
  onTranscriptionComplete: (text: string) => void;
  onTranscriptionError: (error: string) => void;
  mode: AIBuilderMode;
  onModeChange: (mode: AIBuilderMode) => void;
};

export const InputBox = ({
  value,
  placeholder,
  textAreaRef,
  inputRowRef,
  buttonsRowRef,
  onChange,
  onSubmit,
  onResize,
  files,
  onFileSelected,
  onFileRemove,
  isDisabled = false,
  isGenerating = false,
  onInterrupt,
  errorText,
  onClearError,
  selectedNode,
  onTranscriptionComplete,
  onTranscriptionError,
  mode,
  onModeChange,
}: InputBoxProps) => {
  const { t } = useScopedTranslate(locale);
  const transcriptionMutation = useTranscribeAudio();
  const showFilePreviewModal = usePreviewFileModal();
  const dragDepthRef = useRef(0);
  const [isDragActive, setIsDragActive] = useState(false);

  useEffect(() => {
    requestAnimationFrame(onResize);
  }, [files, errorText, selectedNode, onResize]);

  const selectedActionText = useMemo(() => {
    if (!selectedNode) {
      return;
    }

    const label = getActionLabel(selectedNode.type);
    return t('selectedAction', { action: label });
  }, [selectedNode, t]);
  const isPlanMode = mode !== 'agent';

  const handleSubmitOrInterrupt = (focusAfter = false) => {
    if (isGenerating) {
      onInterrupt?.();
      return;
    }

    const trimmedValue = value.trim();
    if (trimmedValue && !isDisabled) {
      onSubmit(trimmedValue);
      if (focusAfter) {
        requestAnimationFrame(() => {
          return textAreaRef.current?.focus();
        });
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitOrInterrupt(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    requestAnimationFrame(onResize);
    onClearError?.();
  };

  const handleSubmitClick = () => {
    handleSubmitOrInterrupt(true);
  };

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardItems = Array.from(e.clipboardData?.items ?? []);
    const imageFiles = clipboardItems
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter((file): file is File => Boolean(file));

    if (!imageFiles.length) return;

    e.preventDefault();
    onClearError?.();

    imageFiles.forEach((file, index) => {
      const normalizedFile = withImageExtension(file, index);
      onFileSelected(normalizedFile, file.name);
    });

    requestAnimationFrame(onResize);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    if (!hasFiles(e)) return;

    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDragActive(true);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!hasFiles(e)) return;

    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';

    if (!isDragActive) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);

    if (dragDepthRef.current === 0) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    const droppedFiles = Array.from(e.dataTransfer.files ?? []);
    if (!droppedFiles.length) return;

    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragActive(false);
    onClearError?.();

    droppedFiles.forEach((file, index) => {
      const normalizedFile = withImageExtension(file, index);
      onFileSelected(normalizedFile, file.name);
    });

    requestAnimationFrame(onResize);
  };

  const handleFilePreview = (fileId: string) => {
    const selectedFile = files.find(({ id }) => id === fileId);
    if (!selectedFile) return;

    const previewFiles = files.map(({ name, previewUrl, file }) => ({
      name,
      src: previewUrl,
      mimeType: file.type,
    }));

    showFilePreviewModal({
      formFile: {
        name: selectedFile.name,
        src: selectedFile.previewUrl,
        mimeType: selectedFile.file.type,
      },
      filesList: previewFiles,
    });
  };

  return (
    <InputDock ref={inputRowRef}>
      {(files.length > 0 || errorText) && (
        <FilesContainer>
          {errorText && <Text color="elementRed100" font="regular12" text={errorText} />}
          {files.map(({ id, name }) => {
            return (
              <FileTag
                key={id}
                name={name}
                onPreview={() => handleFilePreview(id)}
                onRemove={() => onFileRemove(id)}
              />
            );
          })}
        </FilesContainer>
      )}
      <InputRow
        data-input-row="true"
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        $isDragActive={isDragActive}
      >
        <InputTextArea
          ref={textAreaRef}
          value={value}
          onChange={handleChange}
          onPaste={handlePaste}
          placeholder={placeholder ?? t('askAnything')}
          rows={1}
        />
        <Stack space={2} ref={buttonsRowRef}>
          {selectedActionText && (
            <SelectedActionContainer>
              <Text font="regular12" color="textHelper" text={selectedActionText} />
            </SelectedActionContainer>
          )}
          <Stack orientation="horizontal" justify="space-between">
            <Menu
              placement="top-start"
              buttonProps={{
                iconLeft:
                  mode === 'agent' ? (
                    <IconAiAgent variant="gradient" />
                  ) : (
                    <IconAiChatMode variant="gradient" />
                  ),
                iconRight: <IconDropdown />,
                variant: 'secondary',
                size: 'sm',
                text: mode === 'agent' ? t('agentLabel') : t('planLabel'),
              }}
              width={pxToRem(320)}
            >
              <>
                <HeadlessMenu.Item>
                  {({ active }) => (
                    <ModeOptionContainer
                      data-selected={active}
                      onClick={() => onModeChange('agent')}
                    >
                      <Stack
                        orientation="horizontal"
                        space={2}
                        align="center"
                        justify="space-between"
                      >
                        <Stack orientation="horizontal" space={2} align="center">
                          <IconAiAgent
                            variant={mode === 'agent' ? 'solid' : 'gradient'}
                            color={mode === 'agent' ? 'elementBlue100' : 'inherit'}
                          />
                          <Text
                            font="semibold14"
                            color={mode === 'agent' ? 'elementBlue100' : 'textPrimary'}
                            text={t('agentLabel')}
                          />
                        </Stack>
                        {mode === 'agent' && <IconCheckmark color="buttonIconPositive" />}
                      </Stack>
                      <Text font="regular12" color="textSecondary" text={t('agentHelpText')} />
                    </ModeOptionContainer>
                  )}
                </HeadlessMenu.Item>
                <Menu.Separator />
                <HeadlessMenu.Item>
                  {({ active }) => (
                    <ModeOptionContainer
                      data-selected={active}
                      onClick={() => onModeChange('plan')}
                    >
                      <Stack
                        orientation="horizontal"
                        space={2}
                        align="center"
                        justify="space-between"
                      >
                        <Stack orientation="horizontal" space={2} align="center">
                          <IconAiChatMode
                            variant={isPlanMode ? 'solid' : 'gradient'}
                            color={isPlanMode ? 'elementBlue100' : 'textPrimary'}
                          />
                          <Text
                            font="semibold14"
                            color={isPlanMode ? 'elementBlue100' : 'textPrimary'}
                            text={t('planLabel')}
                          />
                        </Stack>
                        {isPlanMode && <IconCheckmark color="buttonIconPositive" />}
                      </Stack>
                      <Text font="regular12" color="textSecondary" text={t('planHelpText')} />
                    </ModeOptionContainer>
                  )}
                </HeadlessMenu.Item>
              </>
            </Menu>
            <Stack orientation="horizontal" space={2} justify="flex-end" align="center">
              <AudioRecorderButton
                onTranscriptionComplete={onTranscriptionComplete}
                onError={onTranscriptionError}
                transcriptionMutation={transcriptionMutation}
                isDisabled={isDisabled}
                size="md"
              />
              <FileIconButton
                key={files.map(({ id }) => id).join('|')}
                fileType="image"
                onFileSelected={onFileSelected}
                size="md"
              />
              <IconButton
                variant="blue-white"
                size="md"
                icon={
                  isGenerating ? (
                    <IconCheckboxChecked size={16} color="white100" />
                  ) : (
                    <IconArrowUp />
                  )
                }
                onClick={handleSubmitClick}
              />
            </Stack>
          </Stack>
        </Stack>
      </InputRow>
    </InputDock>
  );
};

const hasFileExtension = (fileName: string) => {
  return /\.[^.]+$/.test(fileName);
};

const hasFiles = (e: DragEvent<HTMLDivElement>) => {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
};

const withImageExtension = (file: File, index: number) => {
  if (hasFileExtension(file.name)) return file;

  const mimeType = file.type.toLowerCase();
  const extension = getImageExtensionFromMimeType(mimeType);
  const trimmedName = file.name?.trim();
  const baseName = trimmedName || `clipboard-image-${Date.now()}-${index + 1}`;

  return new File([file], `${baseName}${extension}`, {
    type: file.type ?? `image/${extension.slice(1)}`,
    lastModified: file.lastModified ?? Date.now(),
  });
};

const InputDock = styled(Stack).attrs({
  space: 2,
})({
  position: 'absolute',
  left: pxToRem(16),
  right: pxToRem(16),
  bottom: pxToRem(16),
  zIndex: 1,
});

const InputRow = styled(Stack).attrs({
  space: 2,
})<{ $isDragActive: boolean }>(({ $isDragActive }) => ({
  minHeight: pxToRem(56),
  borderRadius: pxToRem(8),
  borderStyle: $isDragActive ? 'dashed' : 'solid',
  borderWidth: pxToRem(1),
  borderColor: $isDragActive ? theme.color.elementBlue100 : theme.color.border10,
  padding: pxToRem(8),
  paddingLeft: pxToRem(16),
  overflow: 'hidden',
  background: $isDragActive ? theme.color.blueTrans5 : theme.color.backgroundLightDark,
  transition: 'border-color 180ms ease, background-color 180ms ease',
  '&:focus-within': {
    borderColor: theme.color.elementBlue100,
  },
}));

const FilesContainer = styled(Stack).attrs({
  space: 2,
})({
  width: '100%',
  maxHeight: pxToRem(200),
  overflowY: 'auto',
});

const SelectedActionContainer = styled.div({
  width: '100%',
  minWidth: 0,
});

const ModeOptionContainer = styled(Stack).attrs({ space: 1 })({
  padding: pxToRem(8),
  borderRadius: pxToRem(4),
  cursor: 'pointer',

  '&[data-selected="true"]': {
    backgroundColor: theme.color.uiTrans5,
  },
});

const InputTextArea = styled.textarea({
  display: 'block',
  width: '100%',
  minHeight: pxToRem(32),
  font: theme.font.regular14,
  lineHeight: 1.4,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: theme.color.textPrimary,
  resize: 'none',
  padding: `${pxToRem(8)} ${pxToRem(8)} 0 0`,
  '&::placeholder': {
    color: theme.color.textHelper,
  },
});

const locale = {
  en: {
    askAnything: 'Ask anything...',
    selectedAction: 'Selected action: {action}',
    agentLabel: 'Agent',
    planLabel: 'Plan',
    agentHelpText:
      'Fast mode for building workflows. Agent immediately adds or updates steps to complete your request',
    planHelpText:
      'Review-first mode. Chat drafts a proposed workflow that you can edit and approve before anything changes',
  },
};
